import { DatePipe } from '@angular/common';
import { Component, inject, input, OnDestroy, OnInit, signal } from '@angular/core';
import { OrderStatusSwitchComponent } from '@app/shared/components/order-status-switch/order-status-switch.component';
import { NewRelic } from '@app/shared/services/newrelic.service';

export interface OrderSummaryHeaderOptions {
	shouldShowOrderStatus?: boolean;
	isCheckoutV2?: boolean;
}

const QR_CODE_CONFIG = {
	width: 200,
	margin: 2,
	errorCorrectionLevel: 'H' as const,
	dark: '#000000',
	light: '#FFFFFF',
} as const;

/** A component that displays the order summary header with company info, order details and QR code. */
@Component({
	standalone: true,
	imports: [OrderStatusSwitchComponent, DatePipe],
	selector: 'g-order-summary-header',
	templateUrl: './order-summary-header.component.html',
	styleUrls: ['./order-summary-header.component.scss'],
})
export class OrderSummaryHeaderComponent implements OnInit, OnDestroy {
	readonly order$ = input.required<api.OrderDto>({ alias: 'order' });
	readonly options$ = input<OrderSummaryHeaderOptions | undefined>(undefined, { alias: 'options' });

	private readonly newRelic = inject(NewRelic);

	readonly qrCodeDataUrl$ = signal('');
	currentDate = new Date();

	/** Check if this is being used in checkout v2 confirmation page */
	get isCheckoutV2(): boolean {
		return this.options$()?.isCheckoutV2 ?? false;
	}

	/** Initialize component and generate QR code */
	ngOnInit(): void {
		if (this.order$()?.order_number) {
			void this.generateQrCode();
		}
	}

	/** Cleanup: revoke object URL to prevent memory leak */
	ngOnDestroy(): void {
		const url = this.qrCodeDataUrl$();
		if (url) {
			URL.revokeObjectURL(url);
		}
	}

	/**
	 * Generates QR code for order URL with optimized settings for print quality
	 */
	private async generateQrCode(): Promise<void> {
		const orderNumber = this.order$()?.order_number;
		if (!orderNumber) {
			this.qrCodeDataUrl$.set('');
			return;
		}

		const orderUrl = `${globalThis.location.origin}/order/${orderNumber}`;

		try {
			// Dynamic import with webpackChunkName for better code splitting
			// Destructuring only toString to signal tree-shaking intent
			const { toString } = await import(
				/* webpackChunkName: "qrcode" */
				/* webpackMode: "lazy" */
				'qrcode'
			);
			const qrCodeSvg = await toString(orderUrl, {
				type: 'svg',
				width: QR_CODE_CONFIG.width,
				margin: QR_CODE_CONFIG.margin,
				errorCorrectionLevel: QR_CODE_CONFIG.errorCorrectionLevel,
				color: {
					dark: QR_CODE_CONFIG.dark,
					light: QR_CODE_CONFIG.light,
				},
			});

			const previousUrl = this.qrCodeDataUrl$();
			if (previousUrl) {
				URL.revokeObjectURL(previousUrl);
			}

			const blob = new Blob([qrCodeSvg], { type: 'image/svg+xml' });
			this.qrCodeDataUrl$.set(URL.createObjectURL(blob));
		} catch (err) {
			this.newRelic.noticeError(err, {
				context: 'QR code generation failed',
				orderNumber,
			});
			this.qrCodeDataUrl$.set('');
		}
	}
}
